import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useMedia } from "@/context/MediaContext";
import { MediaFrame } from "@/components/media/MediaFrame";
import { Eyebrow, Reveal } from "@/components/site/Reveal";

const PARAS = [
  "I started working full time in my family's food manufacturing business at 16.",
  "I wasn't learning marketing from a textbook. I was seeing how products were developed, manufactured, sold into retailers and eventually picked up by customers.",
  "And I kept noticing the same thing.",
];
const PARAS_2 = [
  "When we started putting the camera behind the scenes and showing people the products, processes and personalities behind the business, we saw what content could actually do.",
  "That's where Viral Vision Media came from.",
  "Baxter and I built VVM to bring that same thinking to other businesses.",
];

// Magazine feature: oversized headline, photography that overlaps the column, a pull quote that earns its space.
const Story = () => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : -60]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : 50]);
  const { items, hasReal } = useMedia("story", 0);

  return (
    <section ref={ref} id="story" className="py-32 md:py-48 overflow-hidden" data-testid="story" aria-labelledby="story-heading">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal><Eyebrow>Why we built VVM</Eyebrow></Reveal>
        <Reveal delay={0.05}>
          <h2 id="story-heading" className="mt-7 font-display uppercase text-4xl sm:text-5xl lg:text-[5.5rem] tracking-tight leading-[0.92] text-[#2E1B16] max-w-5xl">
            We came from the businesses we now market.
          </h2>
        </Reveal>

        <div className={`mt-20 md:mt-28 grid lg:grid-cols-12 gap-10 lg:gap-8 items-start`}>
          {/* Photography column */}
          {hasReal && <div className="lg:col-span-6 relative">
            <motion.div style={{ y: y1 }} className="relative z-10 w-[86%]">
              <MediaFrame item={items[0]} ratio="aspect-[4/5]" className="rounded-sm" sizes="(min-width:1024px) 40vw, 90vw" />
            </motion.div>
            <motion.div style={{ y: y2 }} className="absolute right-0 bottom-[-12%] w-[46%] z-20">
              {items[1] && <MediaFrame item={items[1]} ratio="aspect-[3/4]" className="rounded-sm shadow-[0_40px_80px_-30px_rgba(46,27,22,0.4)]" sizes="(min-width:1024px) 22vw, 45vw" />}
            </motion.div>
            <p className="absolute -left-2 top-[-2.5rem] font-annotation text-2xl text-[#2E1B16]/60 rotate-[-4deg]" aria-hidden="true">on site, not in a studio</p>
          </div>}

          {/* Text column */}
          <div className={hasReal ? "lg:col-span-5 lg:col-start-8 lg:pt-16" : "lg:col-span-8 lg:col-start-4"}>
            <div className="space-y-6 text-lg md:text-xl text-[#2E1B16]/75 leading-relaxed">
              {PARAS.map((p, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <p className={i === 0 ? "font-accent text-2xl md:text-[1.75rem] text-[#2E1B16] leading-snug" : ""}>{p}</p>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <blockquote className="my-12 md:my-16 border-l-2 border-[#38BDF8] pl-6 md:pl-8">
                <p className="font-accent text-3xl md:text-4xl leading-[1.15] text-[#2E1B16]">
                  Some of the most interesting businesses had almost nothing online showing what actually made them interesting.
                </p>
              </blockquote>
            </Reveal>

            <div className="space-y-6 text-lg md:text-xl text-[#2E1B16]/75 leading-relaxed">
              {PARAS_2.map((p, i) => (
                <Reveal key={i} delay={i * 0.05}><p className={i === 1 ? "font-display text-[#2E1B16]" : ""}>{p}</p></Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <div className="mt-12 grid sm:grid-cols-[1fr_auto] gap-8 items-end">
                <p className="text-lg md:text-xl text-[#2E1B16] leading-relaxed">
                  Not posting for the sake of posting.<br />
                  Understanding the business first, finding what makes people care, and building the marketing around it.
                </p>
                {items[2] && (
                  <div className="w-36 sm:w-40">
                    <MediaFrame item={items[2]} ratio="aspect-[4/5]" className="rounded-sm" sizes="160px" />
                  </div>
                )}
              </div>
              <p className="mt-8 font-annotation text-2xl text-[#2E1B16]/70">Angela &amp; Baxter</p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Story;
